import { Icon } from '../../../../../../widgets/icon';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import { T } from 'src/shared/helpers';
import ArrowDownIcon from 'src/shared/icons/arrow-down-solid.svg';
import ArrowUpIcon from 'src/shared/icons/arrow-up-solid.svg';
import CheckedCircleIcon from 'src/shared/icons/check-circle-solid.svg';
import CircleIcon from 'src/shared/icons/circle.svg';
import WarningIcon from 'src/shared/icons/exclamation-triangle-solid.svg';
import PreSetIcon from 'src/shared/icons/image.svg';
import LinkIcon from 'src/shared/icons/link-solid.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { ComposerAttributeOptionNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

interface ComposerAttributeTreeElementContextMenuData {
    onContextMenuItemClick: (event: React.MouseEvent<HTMLDivElement>, option: ComposerAttributeOptionNode) => void;
    contextMenu: React.ReactNode;
}

interface ComposerOptionTreeElementProps {
    option: ComposerAttributeOptionNode;
    onSelect: (event: React.MouseEvent<HTMLDivElement>, option: ComposerAttributeOptionNode) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>, option: ComposerAttributeOptionNode) => void;
    onMoveUp?: (option: ComposerAttributeOptionNode) => void;
    onMoveDown?: (option: ComposerAttributeOptionNode) => void;
    onDragStart?: (e: React.DragEvent<HTMLDivElement>, option: ComposerAttributeOptionNode) => void;
    onDragEnd?: (e: React.DragEvent<HTMLDivElement>, option: ComposerAttributeOptionNode) => void;
    onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
    onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
    onSetDefault?: (option: ComposerAttributeOptionNode) => void;
    contextMenu?: ComposerAttributeTreeElementContextMenuData;
    selected: boolean;
    hasLink?: boolean;
    shift?: boolean;
}

const ComposerAttributeTreeElementContainer = styled.div<{
    shift?: boolean;
    selected?: boolean;
}>`
    display: flex;
    align-items: center;
    padding: 4px 8px;
    padding-left: 12px;
    margin-left: ${(props) => (props.shift ? '24px' : '0px')};
    background-color: ${(props) => (props.selected ? theme.app.secondary : 'transparent')};
    border: 1px solid ${(props) => (props.selected ? theme.app.secondary : theme.app.borderColor)};
    color: ${(props) => (props.selected ? 'white' : theme.app.textColor)};
    border-radius: 4px;
    cursor: pointer;
    height: 48px;
    min-height: 48px;
    width: ${(props) => (props.shift ? 'calc(100% - 24px)' : '100%')};
    user-select: none;
    transition:
        background-color 0.2s ease,
        border-color 0.2s ease;

    &:hover {
        background-color: ${theme.app.backgroundColor};
        border-color: ${theme.app.secondary};
        opacity: ${(props) => (props.selected ? 1 : 0.8)};
    }
`;

const ComposerAttributeTreeElementHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: start;
    width: 100%;
    height: 100%;
    position: relative;
`;

const ComposerAttributeTreeElementLeftContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 8px;
    width: calc(100% - 60px); /* Adjusted to account for right container */
    min-width: 0; /* Prevents flex item from overflowing */
`;

const ComposerAttributeTreeElementImageContainer = styled.div`
    width: 24px;
    height: 24px;
    padding: 2px;
    display: flex;
    align-items: center;
    justify-content: center;

    & > img,
    svg {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

const ComposerAttributeTreeElementNameContainer = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ComposerAttributeTreeElementRightContainer = styled.div`
    position: absolute;
    right: 8px;
    display: flex;
    align-items: center;
    flex-shrink: 0; /* Prevents container from shrinking */
`;

const ComposerAttributeTreeElementDefaultContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 24px;
    cursor: pointer;

    svg {
        width: 24px;
        height: 24px;
    }
`;
const TooltipContent = styled.div`
    font-family: 'Inter', 'Nunito', san-serif;
    font-size: 14px;
    text-align: left;
    line-height: 18px;

    a {
        color: ${(props) => props.theme.app.secondary};
        font-weight: bold;
    }
`;
const IconContainer = styled.div`
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
`;

export const ComposerOptionTreeElement: FC<ComposerOptionTreeElementProps> = ({
    option,
    onSelect,
    onDoubleClick,
    contextMenu,
    selected,
    hasLink,
    shift = false,
    onMoveUp,
    onMoveDown,
    onDragStart,
    onDragEnd,
    onDrop,
    onDragOver,
    onSetDefault
}) => {
    const [showContextMenu, setShowContextMenu] = React.useState(false);

    const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
        if (contextMenu) {
            event.preventDefault();
            event.stopPropagation();
            setShowContextMenu(true);
            contextMenu.onContextMenuItemClick(event, option);
        }
    };

    return (
        <>
            <ComposerAttributeTreeElementContainer
                shift={shift}
                selected={selected}
                onClick={(e) => {
                    onSelect(e, option);
                }}
                onDoubleClick={onDoubleClick ? (e) => onDoubleClick(e, option) : undefined}
                onContextMenu={handleContextMenu}
                draggable={!!onDragStart}
                onDragStart={(e) => onDragStart && onDragStart(e, option)}
                onDragEnd={(e) => onDragEnd && onDragEnd(e, option)}
                onDragOver={onDragOver}
                onDrop={onDrop}
            >
                <ComposerAttributeTreeElementHeader>
                    <ComposerAttributeTreeElementLeftContainer>
                        {option.brokenValues.size ? (
                            <Icon
                                tooltip={Array.from(option.brokenValues.values()).map((value) => (
                                    <TooltipContent key={value}>{value}</TooltipContent>
                                ))}
                            >
                                <WarningIcon fill={selected ? 'white' : theme.app.textColor} />
                            </Icon>
                        ) : (
                            <></>
                        )}
                        <ComposerAttributeTreeElementImageContainer>
                            {option.img ? <img src={option.img} alt={option.name} /> : <PreSetIcon fill={selected ? 'white' : theme.app.textColor} />}
                        </ComposerAttributeTreeElementImageContainer>
                        <ComposerAttributeTreeElementNameContainer title={option.name}>{option.name}</ComposerAttributeTreeElementNameContainer>
                    </ComposerAttributeTreeElementLeftContainer>

                    <ComposerAttributeTreeElementRightContainer>
                        {hasLink && (
                            <ZTooltip
                                title={T._('The option has constraints', 'Admin')}
                                html={<TooltipContent>{T._('The option has constraints', 'Admin')}</TooltipContent>}
                            >
                                <IconContainer>
                                    <LinkIcon fill={selected ? 'white' : theme.app.textColor} />
                                </IconContainer>
                            </ZTooltip>
                        )}
                        <ZTooltip title={''} html={<TooltipContent>{T._('Set as default option', 'Admin')}</TooltipContent>}>
                            <ComposerAttributeTreeElementDefaultContainer
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSetDefault && onSetDefault(option);
                                }}
                            >
                                <IconContainer>
                                    {option.isDefault ? (
                                        <CheckedCircleIcon fill={selected ? 'white' : theme.app.textColor} />
                                    ) : (
                                        <CircleIcon fill={selected ? 'white' : theme.app.textColor} />
                                    )}
                                </IconContainer>
                            </ComposerAttributeTreeElementDefaultContainer>
                        </ZTooltip>{' '}
                        <ZTooltip title={''} html={<TooltipContent>{T._('Move up', 'Admin')}</TooltipContent>}>
                            <IconContainer
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onMoveUp && onMoveUp(option);
                                }}
                            >
                                <ArrowUpIcon fill={selected ? 'white' : theme.app.textColor} />
                            </IconContainer>
                        </ZTooltip>
                        <ZTooltip title={''} html={<TooltipContent>{T._('Move down', 'Admin')}</TooltipContent>}>
                            <IconContainer
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onMoveDown && onMoveDown(option);
                                }}
                            >
                                <ArrowDownIcon fill={selected ? 'white' : theme.app.textColor} />
                            </IconContainer>
                        </ZTooltip>
                    </ComposerAttributeTreeElementRightContainer>
                </ComposerAttributeTreeElementHeader>
                {showContextMenu && contextMenu && contextMenu.contextMenu}
            </ComposerAttributeTreeElementContainer>
        </>
    );
};
